import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

/* Wine highlight block — tight to letters, 80% height, sits behind text */
const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      {/* ═══ 1. HERO ═══ */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#D4A0A0] mb-6 block">HOW I THINK ABOUT BRANDING</span>
            <h1 className="mb-8">
              <span className="font-display text-5xl md:text-6xl lg:text-7xl text-white block leading-[0.95]">WHY TRUTH</span>
              <span className="font-accent text-5xl md:text-6xl lg:text-7xl text-[#BE123C] block mt-2 leading-[1.1]">is the strategy</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Most branding advice tells you to polish, perform and position yourself strategically. I think that is exactly why so many brands sound like they were written by the same person. The ones that pull in the best clients, are the ones, where the founder stopped performing... and just said what they actually think.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 2. THE BELIEF ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1210] leading-[1.15]">
                You know when you meet someone and they seem interesting, but <span className="font-accent text-[#BE123C]">something feels slightly off?</span>
              </h2>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
              <p>Maybe they talk about themselves in a way that doesn't match the energy you're getting from them. You cannot point to what is wrong exactly, you just don't fully buy it. That is what a mismatched brand does to your audience every single day.</p>
              <p>Branding is about making sure the outside matches the inside. When that match is missing, people sense it. They might not tell you why they left your website after nine seconds. They just did.</p>
              <p>The fix is never more polish. It is more honesty. The most magnetic version of your brand is the one where someone reads your website, sends the link to their best friend, and says <em>I really like her vibe, I want to work with her</em>.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider 1 */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Clients buy <Highlight>truth</Highlight>. They can smell a scripted brand from three sentences in. But when your website reads like you actually wrote it, sitting at your desk with a coffee, they pay attention and they stay. And they come back with their credit card <Highlight>ready to buy</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 3. WHAT BRANDING ACTUALLY IS ═══ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHAT IS BRAND IDENTITY ACTUALLY</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210]">THINK OF IT LIKE A </span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C]">person</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Brand identity is the personality</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Who you are as a business. What you stand for, how you talk, what you believe in. When someone describes you to a friend, this is what they say.</p>
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Visual identity is the looks</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Your logo, colours, fonts, the way your Instagram grid feels when someone lands on it for the first time. The outfit your brand puts on every morning. It's what people see before they hear a single word from you.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 4. MY STORY ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                <img src="/images/ola-story.jpg" alt="Aleksandra Kałuża" className="w-full h-full object-cover object-top" />
              </div>
              <div className="mt-10">
                <h3 className="font-eyebrow text-[#8B1A3A] mb-4">OUTSIDE OF WORK</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7]">
                  When I close my laptop, I am probably reading a book about human mind, meditating, cooking something delicious with my whole heart, or planning my next spa trip. My friends call me the SPA expert. I take that title a little too seriously. Music is a huge part of my life, and so is Brazil. Together with my partner, we co-run an association bringing Brazilian footvolley and culture to Europe.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">MY STORY</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">I'VE ALWAYS BEEN</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">looking deeper</span>
              </h2>
              <div className="space-y-5 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>The way I work was shaped by two things that seem completely different but ended up feeding each other.</p>
                <div className="border-l-2 border-[#BE123C]/30 pl-6 my-6">
                  <span className="font-eyebrow text-[#8B1A3A] text-xs block mb-3">THE FIRST: STRUCTURE</span>
                  <p>I spent years inside global companies across Europe, leading creative teams of 40+ people and running multi-million-euro campaigns. The kind of job where you present to executives on Monday and your campaign is live across six countries by Friday. That taught me how to build brands that hold up when real money is on the line.</p>
                </div>
                <div className="border-l-2 border-[#BE123C]/30 pl-6 my-6">
                  <span className="font-eyebrow text-[#8B1A3A] text-xs block mb-3">THE SECOND: PERSONAL CHANGE</span>
                  <p>Then life stepped in. A burnout, an accident, and a message from the Universe that was impossible to ignore: this chapter is over. Not a slow fade. One day the life I had built just did not fit anymore. I needed to figure out what the new chapter looks like. What I am actually meant to do now.</p>
                  <p className="mt-4">That search pulled me into psychology, human behaviour, archetypes, identity work, spirituality. I went deep.</p>
                </div>
                <p>What came out was a methodology for finding your own <strong className="text-[#1C1210]">essence</strong>. I learned to recognise where my flow is and rediscover talents that were already inside me, just invisible at full speed. Because we are already complete.</p>
                <p>When I meet a founder doing incredible work and their brand looks like another template from Canva, it genuinely pisses me off. I can see their talent sitting right there & at the same time, every client they are losing because nobody translated the brand properly. So that is what I do now. I help them see who they are, what makes them different, and I translate that into a brand people can feel. The strategy and the visuals.</p>
                <p>And when that clicks, their clients stop comparing prices and stop asking for references. They book a call because something in the brand made them feel <em>this is my person</em>. And that feeling started in the first three seconds.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider 2 */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              The more <Highlight>unfiltered</Highlight> you are, the faster people decide. Your quirks, your strong opinions, that one thing you are afraid to say out loud on your website. That is exactly what makes the right client read your page and think <Highlight>where was she the whole time</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. HOW I WORK ═══ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">HOW I WORK</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">BETWEEN SENSITIVITY</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">and structure</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Before we even get on a call, I have already gone through your website, your Instagram, your offers, the way you write. By the time we sit down, I usually already know where the disconnect is.</p>
                <p>That is when the uncomfortable questions start. The ones your friends won't ask because they are too polite. Like why the most interesting part of what you do is buried three clicks deep on your website.</p>
                <p>For founders who don't yet know who they are in the context of their brand, I run an in-depth identity process I built over years. We find what makes you different and turn it into something your audience picks up in seconds.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:mt-16">
              <div className="bg-[#F5F0EB] rounded-2xl p-8">
                <h3 className="font-eyebrow text-[#8B1A3A] mb-6">WHAT I VALUE</h3>
                <ul className="space-y-4">
                  {[
                    { icon: "\u2600\uFE0E", text: "Truth over performance" },
                    { icon: "\u2658", text: "Courage as initiation" },
                    { icon: "\u2665\uFE0E", text: "Emotional depth over surface" },
                    { icon: "\u2666\uFE0E", text: "Intuition first-approach" },
                    { icon: "\u2655", text: "Quality over quantity" },
                    { icon: "\u2667", text: "Real partnership" },
                    { icon: "\u2650\uFE0E", text: "Respect for energy" },
                  ].map((value, i) => (
                    <li key={i} className="font-serif text-lg text-[#1C1210] flex items-center gap-4">
                      <span className="text-[#BE123C] text-xl w-7 h-7 flex items-center justify-center flex-shrink-0">{value.icon}</span>
                      {value.text}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 6. WHO I WORK WITH ═══ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHO I WORK WITH</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block">PURPOSE-DRIVEN FOUNDERS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1">ready for clarity</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7]">
              Founders and small teams whose work is already good but whose brand has not caught up yet. They are done with sounding generic and ready for a brand that can hold what comes next.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "The Founder At The Start", body: "You know what you do and you are good at it. But when it comes to showing it online, the words feel generic and the visuals look like everyone else. The brand does not sound like you yet.", img: "/images/mood/arch-sea.jpg" },
              { title: "The Founder In Transition", body: "You have grown and got clearer on what matters. But the brand still sounds like an older version of your life. You are building from a different place now and need a brand that reflects that.", img: "/images/mood/water-light.jpg" },
              { title: "The Established Founder", body: "Your business has real traction. People come back, recommend you and trust your work. But your website and Instagram do not reflect the value you give. The brand has not caught up.", img: "/images/mood/wood-amber.jpg" },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="overflow-hidden">
                <div className="h-52 md:h-56 overflow-hidden rounded-t-2xl">
                  <img src={card.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="pt-6 pb-2">
                  <h3 className="font-serif text-lg text-[#1C1210] mb-2">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 7. CTA ═══ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/arch-sea.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#1C1210]/90 backdrop-blur-sm px-10 py-14 md:px-16 md:py-20 rounded-2xl text-center">
              <h2 className="mb-4">
                <span className="font-serif text-3xl md:text-4xl text-white block">Ready to build a brand that</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-2">feels like you?</span>
              </h2>
              <p className="font-body text-base text-white/60 leading-relaxed mb-10 max-w-xl mx-auto">
                If your business is stronger than the way it looks online right now, we should talk. Book a brand clarity call and I will tell you exactly what I see.
              </p>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                BOOK A BRAND CLARITY CALL <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
